// =============================================
// transferRunStore — stan RUNU kreatora "Przenies domeny" poza React.
//
// Router to <BrowserRouter> (brak useBlocker), a Radix odmontowuje nieaktywne
// zakladki — gdyby stan runu zyl w komponencie, klik w sidebar w trakcie
// przepisywania NS osierocilby runTransfer i skasowal statusy wierszy.
// Store modulowy: runner pisze tutaj, UI czyta przez useSyncExternalStore,
// remount CloudflarePage odtwarza krok 4 z aktualnymi statusami.
// isTransferRunning() uzywa AppSidebar / WorkspaceSwitcher do confirm przed nawigacja.
// =============================================
import { useSyncExternalStore } from "react";
import type { RowRunState, TransferPlan, TransferSummary } from "@/services/cloudflare/transferTypes";

export type TransferRunStatus = "idle" | "running" | "done" | "stopped";

export interface TransferRunSnapshot {
  status: TransferRunStatus;
  /** plan zamrozony na czas runu (kolejnosc wierszy, akcje) */
  plan: TransferPlan | null;
  rows: Record<string, RowRunState>;
  summary: TransferSummary | null;
  startedAt: number | null;
  finishedAt: number | null;
}

const EMPTY: TransferRunSnapshot = { status: "idle", plan: null, rows: {}, summary: null, startedAt: null, finishedAt: null };
const LS_LAST_RUN_KEYS = "cf_transfer_last_run_keys";

let snapshot: TransferRunSnapshot = EMPTY;
let stopRequested = false;
const listeners = new Set<() => void>();

const emit = () => { for (const l of listeners) l(); };
const set = (patch: Partial<TransferRunSnapshot>) => { snapshot = { ...snapshot, ...patch }; emit(); };

export const transferRunStore = {
  get: () => snapshot,
  subscribe: (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; },

  /** Start runu: zamraza plan, zeruje wiersze (albo zachowuje poprzednie przy "Ponow"). */
  start(plan: TransferPlan, keepRows: boolean) {
    stopRequested = false;
    set({ status: "running", plan, rows: keepRows ? snapshot.rows : {}, summary: null, startedAt: Date.now(), finishedAt: null });
    try { sessionStorage.setItem(LS_LAST_RUN_KEYS, JSON.stringify(plan.rows.map((r) => r.domain.key))); } catch { /* prywatne okno */ }
  },
  setRow(row: RowRunState) {
    set({ rows: { ...snapshot.rows, [row.key]: row } });
  },
  finish(summary: TransferSummary) {
    set({ status: summary.stoppedReason ? "stopped" : "done", summary, finishedAt: Date.now() });
  },
  requestStop() { stopRequested = true; emit(); },
  isStopRequested: () => stopRequested,
  reset() { stopRequested = false; snapshot = EMPTY; emit(); },

  /** Klucze domen z ostatniego runu (sessionStorage) — fallback po zamknieciu karty. */
  lastRunKeys(): string[] {
    try { const raw = sessionStorage.getItem(LS_LAST_RUN_KEYS); return raw ? (JSON.parse(raw) as string[]) : []; } catch { return []; }
  },
  clearLastRunKeys() { try { sessionStorage.removeItem(LS_LAST_RUN_KEYS); } catch { /* ignore */ } },
};

export const isTransferRunning = () => snapshot.status === "running";

export function useTransferRunStore(): TransferRunSnapshot {
  return useSyncExternalStore(transferRunStore.subscribe, transferRunStore.get, transferRunStore.get);
}

export const TRANSFER_LEAVE_MSG = "Trwa przenoszenie domen (zmiana NS). Opuścić stronę? Run będzie kontynuowany w tle — wrócisz do niego w /cloudflare.";

/** Guard dla Link/onClick w sidebarze: true = mozna nawigowac. */
export function confirmLeaveDuringTransfer(): boolean {
  if (!isTransferRunning()) return true;
  return window.confirm(TRANSFER_LEAVE_MSG);
}
